import { useQuery } from '@tanstack/react-query';
import { cacheProfiles } from './cacheProfiles';
import type { EntityDefinition, EntityQueryOptions, FetcherEntry } from './types';

/**
 * Creates a typed React Query hook from an entity definition and a named fetcher.
 *
 * @example
 * const useProjectActs = createEntityQuery(actsEntity, 'list');
 *
 * // In component:
 * const { data } = useProjectActs(
 *   { projectId: 1, version: 'v2' },
 *   { profile: 'editor' }
 * );
 */
// biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
export function createEntityQuery<TFetchers extends Record<string, FetcherEntry<any, any>>, TKey extends keyof TFetchers & string>(
    entity: EntityDefinition<TFetchers>,
    fetcherKey: TKey,
) {
    type Fetcher = NonNullable<TFetchers[TKey]>;
    // biome-ignore lint/suspicious/noExplicitAny: required for conditional type inference
    type TParams = Fetcher extends FetcherEntry<infer P, any> ? P : never;
    // biome-ignore lint/suspicious/noExplicitAny: required for conditional type inference
    type TData = Fetcher extends FetcherEntry<any, infer D> ? D : never;

    // Safe to cast: TKey is constrained to keyof TFetchers, so the lookup is always defined
    const fetcher = entity.fetchers[fetcherKey] as FetcherEntry<TParams, TData>;

    return function useEntityQuery(params: TParams, options: EntityQueryOptions<TData> = {}) {
        const { profile = 'default', enabled = true, select, overrides } = options;
        const profileConfig = cacheProfiles[profile];

        return useQuery<TData>({
            queryKey: fetcher.key(params) as unknown as unknown[],
            queryFn: () => fetcher.fetch(params),
            enabled,
            select,
            ...profileConfig,
            ...overrides,
        });
    };
}
